import { useId, useMemo, useState } from 'react';
import { Link } from 'react-router';
import type { Mailbox } from '@bekebi/contracts/api';
import { Button, buttonClass } from '../../components/button';
import { ChevronDownIcon } from '../../components/icons';
import { EmptyState } from '../../components/states';
import { useHasPermission } from '../../session/current-user';
import { canChangeSize, formatBytes, formatQuota } from './quota';

/**
 * The mailbox list: search, sorting and pages over exactly what the Mail Server reported. A fact
 * the Mail Server did not report reads "Not reported" — never a default. Creation and last-edit
 * dates are not shown: the Mail Server's API does not provide them.
 */

type SortKey = 'address' | 'used' | 'quota';
type SortDir = 'asc' | 'desc';
const PAGE_SIZES = [10, 25, 50] as const;

const LINK =
  'text-accent-link underline decoration-accent-link/40 underline-offset-4 hover:decoration-accent-link';

export function mailboxPath(address: string): string {
  return `/app/mailboxes/${encodeURIComponent(address)}/size`;
}

function compare(a: Mailbox, b: Mailbox, key: SortKey): number {
  if (key === 'address') return a.address.localeCompare(b.address);
  // No limit sorts as the largest size.
  if (key === 'quota') {
    return (
      (a.quotaBytes ?? Number.POSITIVE_INFINITY) - (b.quotaBytes ?? Number.POSITIVE_INFINITY) || 0
    );
  }
  // Unreported usage sorts after every reported figure, whichever the direction.
  if (a.usedBytes === null || b.usedBytes === null) return 0;
  return a.usedBytes - b.usedBytes;
}

export function MailboxList({ mailboxes }: { mailboxes: Mailbox[] }) {
  const canManage = useHasPermission('mail.mailboxes.manage');
  const unlimited = useHasPermission('mail.mailboxes.manage_unlimited');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<{ key: SortKey; dir: SortDir }>({
    key: 'address',
    dir: 'asc',
  });
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZES[0]);
  const [page, setPage] = useState(0);
  const searchId = useId();
  const sizeId = useId();

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    const matching = term
      ? mailboxes.filter(
          (m) =>
            m.address.toLowerCase().includes(term) ||
            (m.comment ?? '').toLowerCase().includes(term),
        )
      : mailboxes;
    const reported = matching.filter((m) => sort.key !== 'used' || m.usedBytes !== null);
    const unreported = matching.filter((m) => sort.key === 'used' && m.usedBytes === null);
    const sorted = [...reported].sort((a, b) => {
      const order = compare(a, b, sort.key) || a.address.localeCompare(b.address);
      return sort.dir === 'asc' ? order : -order;
    });
    return [...sorted, ...unreported];
  }, [mailboxes, search, sort]);

  const pages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const current = Math.min(page, pages - 1);
  const shown = filtered.slice(current * pageSize, (current + 1) * pageSize);
  const first = filtered.length === 0 ? 0 : current * pageSize + 1;
  const last = current * pageSize + shown.length;

  const toggleSort = (key: SortKey) => {
    setSort((s) =>
      s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' },
    );
    setPage(0);
  };

  const addLink = canManage ? (
    <Link to="/app/mailboxes/new" className={buttonClass('primary')}>
      Add mailbox
    </Link>
  ) : null;

  if (mailboxes.length === 0) {
    return (
      <EmptyState
        title="No mailboxes yet."
        description="The Mail Server reports no mailboxes."
        action={addLink}
      />
    );
  }

  return (
    <section
      aria-label="Mailbox list"
      className="rounded-[var(--radius-card)] border border-line bg-surface"
    >
      <div className="flex flex-col gap-3 border-b border-line p-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label htmlFor={searchId} className="block text-xs font-medium text-muted">
              Search
            </label>
            <input
              id={searchId}
              type="search"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              placeholder="Address or comment"
              className="mt-1 block h-10 w-64 max-w-full rounded-md border border-line-strong bg-surface px-3 text-sm"
            />
          </div>
          <div>
            <label htmlFor={sizeId} className="block text-xs font-medium text-muted">
              Show
            </label>
            <select
              id={sizeId}
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(0);
              }}
              className="mt-1 block h-10 rounded-md border border-line-strong bg-surface px-2 text-sm"
            >
              {PAGE_SIZES.map((n) => (
                <option key={n} value={n}>
                  {n} per page
                </option>
              ))}
            </select>
          </div>
        </div>
        {addLink}
      </div>

      {filtered.length === 0 ? (
        <p className="p-6 text-sm text-muted">No mailbox matches &ldquo;{search.trim()}&rdquo;.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[46rem] text-left text-sm">
            <caption className="sr-only">Mailboxes reported by the Mail Server</caption>
            <thead className="text-xs uppercase tracking-wide text-muted">
              <tr className="border-b border-line">
                <SortHeader label="Email" column="address" sort={sort} onSort={toggleSort} />
                <th scope="col" className="px-4 py-3 font-medium">
                  Retrieval
                </th>
                <SortHeader label="Used" column="used" sort={sort} onSort={toggleSort} />
                <SortHeader label="Size" column="quota" sort={sort} onSort={toggleSort} />
                <th scope="col" className="hidden px-4 py-3 font-medium md:table-cell">
                  Comment
                </th>
                {canManage ? (
                  <th scope="col" className="px-4 py-3 font-medium">
                    <span className="sr-only">Actions</span>
                  </th>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {shown.map((m) => (
                <tr key={m.address} className="border-b border-line last:border-b-0">
                  <th scope="row" className="whitespace-nowrap px-4 py-3 font-semibold text-fg">
                    {m.address}
                  </th>
                  <td className="px-4 py-3">
                    <Features mailbox={m} />
                  </td>
                  <td className="px-4 py-3">
                    <Usage mailbox={m} />
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-fg-2">
                    {formatQuota(m.quotaBytes)}
                  </td>
                  <td className="hidden max-w-[16rem] px-4 py-3 text-fg-2 md:table-cell">
                    {m.comment ? (
                      <span className="break-words">{m.comment}</span>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>
                  {canManage ? (
                    <td className="whitespace-nowrap px-4 py-3 text-right">
                      {canChangeSize(m.quotaBytes, unlimited) ? (
                        <Link
                          to={mailboxPath(m.address)}
                          className={LINK}
                          aria-label={`Change size of ${m.address}`}
                        >
                          Change size
                        </Link>
                      ) : (
                        <span className="text-xs text-muted">Managed by the Admin</span>
                      )}
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line p-4 text-sm">
        <p className="text-muted" aria-live="polite">
          {filtered.length === 0
            ? 'No mailboxes shown'
            : `Showing ${first}–${last} of ${filtered.length} ${filtered.length === 1 ? 'mailbox' : 'mailboxes'}`}
        </p>
        {pages > 1 ? (
          <nav aria-label="Mailbox pages" className="flex items-center gap-2">
            <Button
              variant="secondary"
              onClick={() => setPage(current - 1)}
              disabled={current === 0}
            >
              Previous
            </Button>
            <span className="text-muted">
              Page {current + 1} of {pages}
            </span>
            <Button
              variant="secondary"
              onClick={() => setPage(current + 1)}
              disabled={current >= pages - 1}
            >
              Next
            </Button>
          </nav>
        ) : null}
      </div>
    </section>
  );
}

function SortHeader({
  label,
  column,
  sort,
  onSort,
}: {
  label: string;
  column: SortKey;
  sort: { key: SortKey; dir: SortDir };
  onSort: (key: SortKey) => void;
}) {
  const active = sort.key === column;
  return (
    <th
      scope="col"
      className="px-4 py-3 font-medium"
      aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        onClick={() => onSort(column)}
        className={`inline-flex items-center gap-1 uppercase tracking-wide hover:text-fg ${active ? 'text-fg' : ''}`}
      >
        {label}
        <ChevronDownIcon
          aria-hidden="true"
          className={`size-3.5 transition-transform ${active ? '' : 'opacity-30'} ${active && sort.dir === 'asc' ? 'rotate-180' : ''}`}
        />
      </button>
    </th>
  );
}

function Features({ mailbox }: { mailbox: Mailbox }) {
  const { imapEnabled, pop3Enabled } = mailbox;
  if (imapEnabled === null && pop3Enabled === null) {
    return <span className="text-xs text-muted">Not reported</span>;
  }
  const on = [imapEnabled ? 'IMAP' : null, pop3Enabled ? 'POP3' : null].filter(
    (f): f is string => f !== null,
  );
  if (on.length === 0) return <span className="text-xs text-muted">None</span>;
  return (
    <span className="flex gap-1">
      {on.map((f) => (
        <span
          key={f}
          className="rounded border border-line-strong bg-raised px-1.5 py-px font-mono text-[0.7rem] text-fg-2"
        >
          {f}
        </span>
      ))}
    </span>
  );
}

/** Space used against the size, with a bar only when both usage and a size limit are reported. */
function Usage({ mailbox }: { mailbox: Mailbox }) {
  if (mailbox.usedBytes === null) {
    return <span className="text-xs text-muted">Not reported</span>;
  }
  if (mailbox.quotaBytes === null) {
    return <span className="whitespace-nowrap text-fg-2">{formatBytes(mailbox.usedBytes)}</span>;
  }
  const ratio = mailbox.quotaBytes > 0 ? mailbox.usedBytes / mailbox.quotaBytes : 0;
  const percent = Math.min(100, Math.round(ratio * 100));
  const nearlyFull = ratio >= 0.9;
  return (
    <div className="min-w-[8rem]">
      <span className="whitespace-nowrap text-fg-2">{formatBytes(mailbox.usedBytes)}</span>
      <div
        role="meter"
        aria-label={`Space used by ${mailbox.address}`}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent}
        aria-valuetext={`${percent}% of ${formatBytes(mailbox.quotaBytes)} used`}
        className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-raised"
      >
        <div
          className={`h-full rounded-full ${nearlyFull ? 'bg-warning' : 'bg-accent-link'}`}
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  );
}
